import { useMemo } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import type { PaginationMeta } from '~/interfaces/community';

export interface MemberFeedPage<T> {
  items: T[];
  meta: PaginationMeta;
  reason?: string | null;
}

interface UseMemberPagedFeedOptions<T> {
  queryKey: readonly unknown[];
  fetchPage: (cursor: string | undefined) => Promise<MemberFeedPage<T>>;
  initialItems?: T[];
  initialMeta?: PaginationMeta;
  initialReason?: string | null;
  enabled?: boolean;
}

const nextPageCursor = <T>(lastPage: MemberFeedPage<T>): string | undefined => {
  if (lastPage.reason || !lastPage.meta.has_more) return undefined;
  return lastPage.meta.next_cursor ?? undefined;
};

export const useMemberPagedFeed = <T>({
  queryKey,
  fetchPage,
  initialItems,
  initialMeta,
  initialReason,
  enabled = true,
}: UseMemberPagedFeedOptions<T>) => {
  const hasInitialData = initialItems !== undefined && initialMeta !== undefined;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError, isError, isLoading } =
    useInfiniteQuery({
      queryKey,
      queryFn: ({ pageParam }: { pageParam: string | undefined }) => fetchPage(pageParam),
      getNextPageParam: nextPageCursor,
      initialPageParam: undefined as string | undefined,
      initialData: hasInitialData
        ? { pages: [{ items: initialItems, meta: initialMeta, reason: initialReason }], pageParams: [undefined] }
        : undefined,
      initialDataUpdatedAt: hasInitialData ? Date.now() : undefined,
      staleTime: 60_000,
      enabled,
    });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  return {
    items,
    pages: data?.pages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isError,
    isLoading,
  };
};
